import React, { useState } from 'react'
import { Mail, Phone, Clock } from 'lucide-react'
import { Button } from '@/components/ui/button'

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  })

  const [formStatus, setFormStatus] = useState<{
    type: 'success' | 'error' | null
    message: string | null
  }>({ type: null, message: null })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.name || !formData.email || !formData.message) {
      setFormStatus({
        type: 'error',
        message: 'Por favor, preencha todos os campos obrigatórios'
      })
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email)) {
      setFormStatus({
        type: 'error',
        message: 'Por favor, insira um endereço de e-mail válido'
      })
      return
    }

    setFormStatus({
      type: 'success',
      message: 'Obrigado! Recebemos sua mensagem e entraremos em contato em breve.'
    })

    setFormData({
      name: '',
      email: '',
      phone: '',
      message: ''
    })

    setTimeout(() => {
      setFormStatus({ type: null, message: null })
    }, 5000)
  }

  return (
    <section id="contact" className="py-20 bg-gray-100">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">Entre em Contato</h2>
          <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
            Pronto para transformar sua situação tributária? Entre em contato com nossa equipe de especialistas hoje.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-xl overflow-hidden">
          <div className="grid md:grid-cols-2">
            <div className="bg-primary p-8 md:p-12 text-primary-foreground">
              <h3 className="text-2xl font-bold mb-6">Fale Conosco</h3>
              <p className="mb-8 text-blue-100">
                Preencha o formulário e nossa equipe entrará em contato em até 24 horas.
              </p>

              <div className="space-y-6">
                <div className="flex items-start">
                  <Mail className="h-6 w-6 text-blue-300 mr-4 mt-1" />
                  <div>
                    <p className="font-medium">E-mail</p>
                    <a href="mailto:contato@taxpro.com.br" className="text-blue-200 hover:text-white transition-colors">
                      contato@taxpro.com.br
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <Phone className="h-6 w-6 text-blue-300 mr-4 mt-1" />
                  <div>
                    <p className="font-medium">Telefone</p>
                    <a href="tel:+551155555555" className="text-blue-200 hover:text-white transition-colors">
                      (11) 5555-5555
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <Clock className="h-6 w-6 text-blue-300 mr-4 mt-1" />
                  <div>
                    <p className="font-medium">Horário de Atendimento</p>
                    <p className="text-blue-200">Segunda - Sexta: 9h às 18h</p>
                  </div>
                </div>
              </div>

              <div className="mt-12 border-t border-blue-600 pt-8">
                <p className="font-medium mb-4">Nosso Escritório</p>
                <address className="not-italic text-blue-200">
                  Av. Paulista, 1000, Sala 400
                  <br />
                  Bela Vista
                  <br />
                  São Paulo, SP 01310-100
                </address>
              </div>
            </div>

            <div className="p-8 md:p-12">
              <form onSubmit={handleSubmit}>
                {formStatus.message && (
                  <div
                    className={`mb-6 p-4 rounded-md ${
                      formStatus.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {formStatus.message}
                  </div>
                )}

                <div className="mb-6">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Nome <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    E-mail <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Telefone
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                    Mensagem <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    required
                  ></textarea>
                </div>

                <Button type="submit" size="lg" className="w-full">
                  Enviar Mensagem
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Contact

